/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@mui/material';
import { Chart2Div } from 'components/layouts/ContentLayout';
import { findMpProgressSummaryItem } from 'apis/mp/MpTracking';

import {
  FlexChart,
  FlexChartLegend,
  FlexChartSeries,
  FlexChartAxis,
  FlexChartDataLabel,
} from '@grapecity/wijmo.react.chart';
import { FlexChartAnimation } from '@grapecity/wijmo.react.chart.animation';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from '../../components/layouts/SearchBox';
import { ComboBox } from '../../components/selects/ComboBox';
import { Code } from '../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../apis/admin/CommonCode';
import { getYearList } from '../../apis/gtng/GatingRegist';
import { DatePickerWrap } from '../../components/inputs/DatePicker';
import Datepicker from 'react-tailwindcss-datepicker';
import { useMessageBar } from '../../hooks/useMessageBar';
import styled from '@emotion/styled';
import { st } from '../../components/inputs/CustomInput';
import _, { isEmpty, isNull } from 'lodash';
import SearchBoxButtons from '../../components/buttons/SearchBoxButtons';
import { MpItemTpCd } from 'models/mp/MpItem';
import { useNavigate } from 'react-router-dom';

export interface condition {
  searchYear?: string;
  perdDivs?: string;
  startDate?: string;
  endDate?: string;
  perdDivsOption?: string;
  dateYn?: string;
}

const MpProgressSummaryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<condition>({});
  const [errors, setErrors] = useState<any>({});
  const [isRequiredYear, setRequiredYear] = useState<boolean>();
  const [isRequiredPerDivsOption, setRequiredPerDivsOption] = useState<boolean>();
  const { openMessageBar } = useMessageBar();

  // graph
  const [chartData, setChartData] = useState<any>([]);
  const chartStyle = {
    height: '332px',
  };
  const SEPARATOR = '\u241F';

  const convertChartData = (mpItemTpCd) => {
    const totalCnt = (chartData || [])
      .filter((o) => o.MP_ITEM_TP_CD === mpItemTpCd)
      .reduce((acc, cur) => acc + cur.CNT, 0);
    return [
      (chartData || [])
        .filter((o) => o.MP_ITEM_TP_CD === mpItemTpCd)
        .reduce(
          (acc, cur) => ({
            ...acc,
            [`${cur.ITEM_STAT_CD}`]: cur.CNT,
            [`${cur.ITEM_STAT_CD}_NM`]: cur.KEY,
            [`${cur.ITEM_STAT_CD}_PERCENT`]: `${((cur.CNT / totalCnt) * 100)
              .toFixed(1)
              .toLocaleString()}%`,
          }),
          { mpItemTpCd: mpItemTpCd, totalCnt: totalCnt }
        ),
    ];
  };

  const convertDefaultSeries = (mpItemTpCd) => {
    return (code?.mpItemStatMap || [])
      .filter((o) => o.optValCtn1 === mpItemTpCd)
      .reduce(
        (acc, cur) => ({
          ...acc,
          [cur.optValCtn2]: cur.cmnCdNm,
        }),
        {}
      );
  };

  const convertSeries = (mpItemTpCd) => {
    const rows = convertChartData(mpItemTpCd);
    if (rows.length > 0) {
      const data = rows[0];
      const keys = Object.keys(data).filter(
        (o) =>
          !['mpItemTpCd', 'totalCnt'].includes(o) && !o.endsWith('_PERCENT') && !o.endsWith('_NM')
      );
      if (keys.length > 0) {
        return keys.reduce(
          (acc, cur) => ({
            ...acc,
            [cur]: data[`${cur}_NM`] || cur,
          }),
          {}
        );
      }
    }
    return convertDefaultSeries(mpItemTpCd);
  };

  /**
   * 차트 데이터
   */
  const niChartData = useMemo(() => {
    return convertChartData(MpItemTpCd.NI); // 신규개선
  }, [chartData]);

  const mpChartData = useMemo(() => {
    return convertChartData(MpItemTpCd.MP); // 설비변경점
  }, [chartData]);

  const heChartData = useMemo(() => {
    return convertChartData(MpItemTpCd.HE); // 수평전개
  }, [chartData]);

  const mpHeChartData = useMemo(() => {
    return convertChartData(MpItemTpCd.MPHE); // 설비변경점/수평전개
  }, [chartData]);

  const spshChartData = useMemo(() => {
    return convertChartData(MpItemTpCd.SPSH); // 제작사양서
  }, [chartData]);

  /**
   * Series
   */
  const niSeries = useMemo(() => {
    return convertSeries(MpItemTpCd.NI); // 신규개선
  }, [code?.mpItemStatMap, chartData]);

  const mpSeries = useMemo(() => {
    return convertSeries(MpItemTpCd.MP); // 설비변경점
  }, [code?.mpItemStatMap, chartData]);

  const heSeries = useMemo(() => {
    return convertSeries(MpItemTpCd.HE); // 수평전개
  }, [code?.mpItemStatMap, chartData]);

  const mpHeSeries = useMemo(() => {
    return convertSeries(MpItemTpCd.MPHE); // 설비변경점/수평전개
  }, [code?.mpItemStatMap, chartData]);

  const spshSeries = useMemo(() => {
    return (code?.chgProgStatCd || []).reduce(
      (acc, cur) => ({
        ...acc,
        [`${cur.cmnCd}`]: cur.cmnCdNm,
      }),
      {} as any
    );
  }, [code?.chgProgStatCd]);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  const getCommonCodes = async () => {
    const searchYearGet = await getYearList();
    const perdDivs: Code[] = await getCommonCodeNames('PERD_DIVS'); // 기준 (년/반기/분기/월)
    const mpItemStatMap: Code[] = await getCommonCodeNames('MP_ITEM_STAT_MAP');
    const chgProgStatCd: Code[] = await getCommonCodeNames('CHG_PROG_STAT_CD');

    const searchYear = searchYearGet.reduce(
      (acc, cur) => [...acc, { cmnCd: String(cur.STARTYEAR), cmnCdNm: String(cur.STARTYEAR) }],
      [] as Code[]
    );

    setCode({
      ...code,
      searchYear: searchYear,
      perdDivs: perdDivs,
      mpItemStatMap: mpItemStatMap,
      chgProgStatCd: chgProgStatCd,
    });
  };

  const handleSearch = () => {
    if (
      !_.isEmpty(condition.perdDivs) &&
      (_.isNull(condition.searchYear) || _.isEmpty(condition.searchYear))
    ) {
      setRequiredYear(true);
      setErrors((prev) => ({
        ...prev,
        searchYear: true,
      }));
      return;
    }
    // 상세 기간 조건을 선택 안할 경우..
    if (
      !_.isEmpty(condition.perdDivs) &&
      condition.perdDivs !== 'YY' &&
      (_.isEmpty(condition.perdDivsOption) || _.isNull(condition.perdDivsOption))
    ) {
      setRequiredPerDivsOption(true);
      setErrors((prev) => ({
        ...prev,
        perdDivsOption: true,
      }));
      return;
    }

    findMpProgressSummaryItem(condition).then((result) => {
      if (result !== null) {
        setChartData(result.chart);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const changePerdDivsOption = async (option?: string) => {
    if (option) {
      console.log('---');
    }
  };

  const changePerdDivs = async (perdDivsVal?: string) => {
    let perdDivsOption = [] as Code[];

    setRequiredPerDivsOption(true);
    switch (perdDivsVal) {
      case 'HLF':
        perdDivsOption = [
          { cmnCd: 'hlf1', cmnCdNm: '상반기' },
          { cmnCd: 'hlf2', cmnCdNm: '하반기' },
        ];
        break;
      case 'QTR':
        perdDivsOption = [
          { cmnCd: 'qtr1', cmnCdNm: '1분기' },
          { cmnCd: 'qtr2', cmnCdNm: '2분기' },
          { cmnCd: 'qtr3', cmnCdNm: '3분기' },
          { cmnCd: 'qtr4', cmnCdNm: '4분기' },
        ];
        break;
      case 'MM':
        perdDivsOption = [
          { cmnCd: '1', cmnCdNm: '1' },
          { cmnCd: '2', cmnCdNm: '2' },
          { cmnCd: '3', cmnCdNm: '3' },
          { cmnCd: '4', cmnCdNm: '4' },
          { cmnCd: '5', cmnCdNm: '5' },
          { cmnCd: '6', cmnCdNm: '6' },
          { cmnCd: '7', cmnCdNm: '7' },
          { cmnCd: '8', cmnCdNm: '8' },
          { cmnCd: '9', cmnCdNm: '9' },
          { cmnCd: '10', cmnCdNm: '10' },
          { cmnCd: '11', cmnCdNm: '11' },
          { cmnCd: '12', cmnCdNm: '12' },
        ];
        break;
      default:
        setRequiredPerDivsOption(false);
        break;
    }

    setCode((prev) => ({
      ...prev,
      perdDivsOption: perdDivsOption,
    }));
  };

  const handleResetCondition = () => {
    setCondition(
      (prev) => Object.keys(prev).reduce((acc, cur) => Object.assign(acc, { [cur]: '' }), {}) as any
    );
  };

  // graph
  const palette = [
    'rgb(180, 181, 243, 0.99)',
    'rgb(218, 240, 136, 0.99)',
    'rgb(252, 169, 191, 0.99)',
    'rgb(173, 206, 255, 0.99)',
    'rgb(241, 244, 243, 0.99)',
    'rgb(230, 222, 249, 0.99)',
  ];

  const onChartRendered = (chart) => {
    const svg: SVGSVGElement | null = chart?.hostElement?.querySelector('svg');
    if (!svg) return;
    const seriesGroups = svg.querySelectorAll<SVGGElement>('.wj-series-group');
    const labelGroups = svg.querySelectorAll<SVGGElement>('.wj-data-labels');
    seriesGroups.forEach((sg, si) => {
      const lg = labelGroups[si];
      if (!lg) return;

      const bars = sg.querySelectorAll<SVGRectElement>('rect');
      const texts = lg.querySelectorAll<SVGTextElement>('text');

      texts.forEach((textEl, pi) => {
        const bar = bars[pi];
        if (!bar) return;

        // 막대 상단 중앙 좌표 계산
        const bx = parseFloat(bar.getAttribute('x') || '0');
        const by = parseFloat(bar.getAttribute('y') || '0');
        const bw = parseFloat(bar.getAttribute('width') || '0');

        const cx = bx + bw / 2 - 26;
        const ty = by + 18;

        const raw = textEl.textContent ?? '';
        if (!raw.includes(SEPARATOR)) return;

        textEl.textContent = '';
        textEl.setAttribute('x', String(cx));
        textEl.setAttribute('y', String(ty));

        // 줄 분리 후 <tspan> 추가
        const lines = raw.split(SEPARATOR);
        lines.forEach((ln, idx) => {
          const t = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          let cx = 0; // x 보정값
          if (idx > 0) {
            const v = Number(lines[0]);
            if (v < 2) {
              cx = 10;
            } else if (Number(lines[0]) < 10) {
              cx = 2;
            }
          }
          t.setAttribute('dx', idx === 0 ? (Number(ln) === 1 ? '5' : '0') : String(-32 + cx));
          t.setAttribute('dy', idx === 0 ? '0' : '16');
          t.textContent = ln;

          // 줄별 스타일
          if (idx === 0) t.setAttribute('style', 'font-weight:600;');
          else t.setAttribute('style', 'fill:#666;');

          textEl.appendChild(t);
        });
      });
    });
  };

  const onLabelRendering = (d, e) => {
    const { y, item, series } = e.hitTestInfo;
    const binding = series.binding;
    e.text = `${y}${SEPARATOR}(${item[`${binding}_PERCENT`]})`;
  };

  const onSelectionChanged = (s, e) => {
    if (s.selection) {
      const binding = s.selection.binding; // 상태
      const mpItemTpCd = s.selection.collectionView.currentItem?.mpItemTpCd;
      s.selection = null;
      navigate('/mp/item', {
        state: {
          itemStatCd: mpItemTpCd === MpItemTpCd.SPSH ? '' : binding,
          mpItemTpCd: mpItemTpCd === MpItemTpCd.SPSH ? '' : mpItemTpCd,
          spshRevCtn: mpItemTpCd === MpItemTpCd.SPSH ? binding : '',
        },
      });
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('com.label.기준년도', '기준년도'))}</label>
                <ComboBox
                  options={code?.searchYear}
                  disabled={condition.dateYn === 'Y'}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.searchYear}
                  msgError={String(
                    t('com.msg.기준년도를 선택해 주세요.', '기준년도를 선택해 주세요.')
                  )}
                  isError={errors?.searchYear}
                  onChange={(value) => {
                    if (isRequiredYear) {
                      setErrors((prev) => ({
                        ...prev,
                        searchYear: false,
                      }));
                    }
                    setCondition({
                      ...condition,
                      searchYear: value,
                    });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('com.label.기간', '기간'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  disabled={condition.dateYn === 'Y'}
                  options={code?.perdDivs}
                  defaultValue={condition?.perdDivs}
                  onChange={(value) => {
                    setCondition({
                      ...condition,
                      perdDivs: value as string,
                    });
                    changePerdDivs(value);
                  }}
                />
                <ComboBox
                  options={code?.perdDivsOption}
                  disabled={condition.dateYn === 'Y'}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition?.perdDivsOption}
                  msgError={String(
                    t('com.msg.상세기간을 선택해 주세요.', '상세기간을 선택해 주세요.')
                  )}
                  isError={errors?.perdDivsOption}
                  onChange={(value) => {
                    if (isRequiredPerDivsOption) {
                      setErrors((prev) => ({
                        ...prev,
                        perdDivsOption: false,
                      }));
                    }
                    setCondition({
                      ...condition,
                      perdDivsOption: value as string,
                    });
                    // changePerdDivsOption(value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <Checkbox
                    id="fstrYn"
                    name="fstrYn"
                    css={st.checkbox}
                    className="custom"
                    checked={condition.dateYn === 'Y'}
                    onClick={(e) => {
                      setCondition({
                        ...condition,
                        dateYn: condition.dateYn === 'Y' ? 'N' : 'Y',
                        startDate: '',
                        endDate: '',
                        searchYear: '',
                        perdDivs: '',
                        perdDivsOption: '',
                      });
                      setRequiredYear(false);
                      setRequiredPerDivsOption(false);
                    }}
                    disableRipple
                  />
                  &nbsp; {t('ut.label.직접입력', '직접입력')}
                </label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    disabled={condition.dateYn != 'Y'}
                    value={{
                      startDate: condition?.startDate || null,
                      endDate: condition?.endDate || null,
                    }}
                    onChange={(value) => {
                      setCondition({
                        ...condition,
                        startDate: String(value?.startDate || ''), // newValue.startDate.toString(),
                        endDate: String(value?.endDate || ''), //newValue.endDate.toString(),
                      });
                    }}
                    i18n={'ko'}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                  ></Datepicker>
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            // defaultCondition={{
            //   startDataInsDtm: dayjs().add(-2, 'year').format('YYYY.MM.DD'),
            //   endDataInsDtm: dayjs().format('YYYY.MM.DD'),
            // }}
            onReset={handleResetCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <Chart3Div>
        <div className={'width20p'}>
          <FlexChart
            header={String(t('mp.label.신규개선', '신규개선'))}
            bindingX={'itemStatCdNm'}
            itemsSource={niChartData}
            palette={palette}
            style={chartStyle}
            rendered={onChartRendered}
            selectionMode="Point"
            selectionChanged={onSelectionChanged}
          >
            <FlexChartLegend position="Bottom" />
            {(Object.keys(niSeries) || []).map((key, idx) => (
              <FlexChartSeries key={`ni_${idx}`} name={niSeries[key]} binding={key} />
            ))}
            <FlexChartAxis wjProperty="axisX" labels={false} />
            <FlexChartDataLabel content="{y}" position="Bottom" rendering={onLabelRendering} />
            <FlexChartAnimation />
          </FlexChart>
        </div>
        <div className={'width40p'}>
          <FlexChart
            header={String(t('mp.label.설비변경점', '설비변경점'))}
            bindingX={'itemStatCdNm'}
            itemsSource={mpChartData}
            palette={palette}
            style={chartStyle}
            rendered={onChartRendered}
            selectionMode="Point"
            selectionChanged={onSelectionChanged}
          >
            <FlexChartLegend position="Bottom" />
            {(Object.keys(mpSeries) || []).map((key, idx) => (
              <FlexChartSeries key={`mp_${idx}`} name={mpSeries[key]} binding={key} />
            ))}
            <FlexChartAxis wjProperty="axisX" labels={false} />
            <FlexChartDataLabel content="{y}" position="Bottom" rendering={onLabelRendering} />
            <FlexChartAnimation />
          </FlexChart>
        </div>
        <div className={'width40p'}>
          <FlexChart
            header={String(t('mp.label.수평전개', '수평전개'))}
            bindingX={'itemStatCdNm'}
            itemsSource={heChartData}
            palette={palette}
            style={chartStyle}
            rendered={onChartRendered}
            selectionMode="Point"
            selectionChanged={onSelectionChanged}
          >
            <FlexChartLegend position="Bottom" />
            {(Object.keys(heSeries) || []).map((key, idx) => (
              <FlexChartSeries key={`he_${idx}`} name={heSeries[key]} binding={key} />
            ))}
            <FlexChartAxis wjProperty="axisX" labels={false} />
            <FlexChartDataLabel content="{y}" position="Bottom" rendering={onLabelRendering} />
            <FlexChartAnimation />
          </FlexChart>
        </div>
      </Chart3Div>
      <Chart2Div>
        <div>
          <FlexChart
            header={String(t('mp.label.설비변경점/수평전개', '설비변경점/수평전개'))}
            bindingX={'itemStatCdNm'}
            itemsSource={mpHeChartData}
            palette={palette}
            style={chartStyle}
            rendered={onChartRendered}
            selectionMode="Point"
            selectionChanged={onSelectionChanged}
          >
            <FlexChartLegend position="Bottom" />
            {(Object.keys(mpHeSeries) || []).map((key, idx) => (
              <FlexChartSeries key={`mpHe_${idx}`} name={mpHeSeries[key]} binding={key} />
            ))}
            <FlexChartAxis wjProperty="axisX" labels={false} />
            <FlexChartDataLabel content="{y}" position="Bottom" rendering={onLabelRendering} />
            <FlexChartAnimation />
          </FlexChart>
        </div>
        <div>
          <FlexChart
            header={String(t('mp.label.제작사양서 개정', '제작사양서 개정'))}
            bindingX={'itemStatCdNm'}
            itemsSource={spshChartData}
            palette={palette}
            style={chartStyle}
            rendered={onChartRendered}
            selectionMode="Point"
            selectionChanged={onSelectionChanged}
          >
            <FlexChartLegend position="Bottom" />
            {(Object.keys(spshSeries) || []).map((key, idx) => (
              <FlexChartSeries key={`spsh_${idx}`} name={spshSeries[key]} binding={key} />
            ))}
            <FlexChartAxis wjProperty="axisX" labels={false} />
            <FlexChartDataLabel content="{y}" position="Bottom" rendering={onLabelRendering} />
            <FlexChartAnimation />
          </FlexChart>
        </div>
      </Chart2Div>
    </>
  );
};

export const Chart3Div = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  & > div {
    &.width20p {
      width: 20%;
    }
    &.width40p {
      width: 40%;
    }
    max-width: calc(100% / 2.6) !important;
  }
`;
export default MpProgressSummaryPage;
